'use client';

// Design App fork — the authenticated agent-app session (user profile + brand
// theme), delivered by x-api at launch-token exchange and stored on the signed
// session cookie. Read it from `/api/session`, cache it process-wide, and:
//   - apply the workspace's brand primary colour to open-design's --accent ramp
//     (so the Design App matches every other 42bucks app), and
//   - feed the nav-rail user component (replacing the upstream Help launcher).
import { useEffect, useState } from 'react';

export interface AgentAppSessionUser {
  id: string;
  name: string | null;
  email: string | null;
  avatarUrl: string | null;
}

export interface AgentAppSessionData {
  user: AgentAppSessionUser;
  theme: {
    colorMode: 'system' | 'light' | 'dark';
    primary: string | null;
    backgroundLight: string | null;
    backgroundDark: string | null;
  };
  workspaceId: string;
  role: 'ADMIN' | 'MEMBER';
}

let cachedSession: AgentAppSessionData | null = null;
let inflight: Promise<AgentAppSessionData | null> | null = null;

export async function loadAgentAppSession(): Promise<AgentAppSessionData | null> {
  if (cachedSession) return cachedSession;
  if (!inflight) {
    inflight = (async () => {
      try {
        const res = await fetch('/api/session', { credentials: 'include' });
        if (!res.ok) return null;
        const json = (await res.json()) as { authenticated?: boolean; session?: AgentAppSessionData };
        return json?.authenticated && json.session ? json.session : null;
      } catch {
        return null;
      }
    })();
  }
  const session = await inflight;
  cachedSession = session;
  return session;
}

// React hook: returns the cached session (or null until/if it loads).
export function useAgentAppSession(): AgentAppSessionData | null {
  const [session, setSession] = useState<AgentAppSessionData | null>(cachedSession);
  useEffect(() => {
    if (cachedSession) {
      setSession(cachedSession);
      return;
    }
    let active = true;
    void loadAgentAppSession().then((s) => {
      if (active) setSession(s);
    });
    return () => {
      active = false;
    };
  }, []);
  return session;
}

const BRAND_ACCENT_VARS = ['--accent', '--accent-hover', '--accent-strong', '--accent-tint'] as const;

// Paint open-design's accent ramp from a single brand hex — same idea as x-ui's
// useBrandTheme, mapped onto this fork's --accent / --accent-hover / --accent-
// strong / --accent-tint tokens (styles/tokens.css). Set them as INLINE styles
// on :root: an injected <style> only ties tokens.css's own :root rule on
// specificity and can lose on cascade order (and tokens.css's chunk may load
// after us); an inline style on the root element beats every stylesheet rule.
export function applyBrandTheme(primary: string | null | undefined): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (!primary || !/^#[0-9a-fA-F]{6}$/.test(primary)) {
    for (const v of BRAND_ACCENT_VARS) root.style.removeProperty(v);
    return;
  }
  root.style.setProperty('--accent', primary);
  root.style.setProperty('--accent-hover', `color-mix(in srgb, ${primary} 84%, #000)`);
  root.style.setProperty('--accent-strong', `color-mix(in srgb, ${primary} 84%, #000)`);
  root.style.setProperty('--accent-tint', `color-mix(in srgb, ${primary} 12%, #fff)`);
}

const BRAND_BG_STYLE_ID = 'brand-canvas-background';
const isBrandHex = (v: unknown): v is string =>
  typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v);

// Paint the workspace brand's canvas background per colour mode — same idea as
// x-ui's useBrandTheme (backgroundLight/backgroundDark -> --ui-bg). We override
// only the page canvas (--bg / --bg-app); panels and elevated chrome stay on the
// neutral ramp, matching x-ui (it keeps elevated chrome neutral). A per-mode
// <style> is required because the value differs by mode; `html[data-theme=...]`
// out-specifies tokens.css's `[data-theme]` / `html:not([data-theme])` rules,
// and appending to <head> wins any specificity tie on cascade order.
export function applyBrandBackground(
  backgroundLight: string | null | undefined,
  backgroundDark: string | null | undefined,
): void {
  if (typeof document === 'undefined') return;
  const light = isBrandHex(backgroundLight) ? backgroundLight : null;
  const dark = isBrandHex(backgroundDark) ? backgroundDark : null;
  const existing = document.getElementById(BRAND_BG_STYLE_ID);
  if (!light && !dark) {
    existing?.remove();
    return;
  }
  const rules: string[] = [];
  if (light) {
    rules.push(`html[data-theme="light"]{--bg:${light};--bg-app:${light};}`);
    rules.push(
      `@media (prefers-color-scheme: light){html:not([data-theme]){--bg:${light};--bg-app:${light};}}`,
    );
  }
  if (dark) {
    rules.push(`html[data-theme="dark"]{--bg:${dark};--bg-app:${dark};}`);
    rules.push(
      `@media (prefers-color-scheme: dark){html:not([data-theme]){--bg:${dark};--bg-app:${dark};}}`,
    );
  }
  const style = (existing as HTMLStyleElement | null) ?? document.createElement('style');
  style.id = BRAND_BG_STYLE_ID;
  style.textContent = rules.join('');
  if (!existing) document.head.appendChild(style);
}
